import React, { useState, useCallback } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Snackbar,
    Alert,
    Stack,
    TextField,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import PublishIcon from '@mui/icons-material/Publish';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { useDeficiencies } from '../../../Pages/Deficiencies/DeficiencyContext';
import {
    TRANSITION_ACTIONS,
    getCorrectTransitionAction,
} from '../../../Pages/Deficiencies/constants';
import AssigneeModal from '../../../Pages/Deficiencies/Modals/AssigneeModal';
import {
    isDeficiencyWorkRole,
    isExternalOwnerConsultantRole,
    isInternalManagerRole,
} from '../../../../utils/roles';

/**
 * NextActionButton Component
 * Self-contained, context-aware button showing the most logical next action
 * for a deficiency based on its current status, metadata, and user role.
 * Handles all workflow logic internally, including modals and notifications.
 *
 * @param {Object} props - Component props
 * @param {Object} props.deficiency - The deficiency object
 * @param {boolean} props.renderSection - Whether to render the sidebar section wrapper
 * @param {Object} props.buttonProps - Additional button props
 * @returns {React.ReactElement|null}
 */
const NextActionButton = ({ deficiency, renderSection = false, ...buttonProps }) => {
    const { state, transitionDeficiency, rejectTerminal, rejectWork } = useDeficiencies();
    const { currentUser } = state;

    // Internal state for modals and notifications
    const [modalState, setModalState] = useState({
        assignModalOpen: false,
        approveModalOpen: false,
        rejectModalOpen: false,
        activeTransition: null,
    });

    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'info',
    });

    const [rejectReason, setRejectReason] = useState('');

    // Notification helper - must be defined before other hooks that use it
    const showNotification = useCallback((message, severity = 'info') => {
        setNotification({
            open: true,
            message,
            severity,
        });
    }, []);

    // Handler functions for different actions
    const handleAccept = useCallback(
        (deficiencyId, transition) => {
            transitionDeficiency(deficiencyId, transition);
            showNotification('Deficiency accepted', 'success');
        },
        [transitionDeficiency, showNotification]
    );

    const handleAssign = useCallback((deficiencyId, transition) => {
        setModalState({
            assignModalOpen: true,
            approveModalOpen: false,
            rejectModalOpen: false,
            activeTransition: transition,
        });
    }, []);

    const handleFinish = useCallback(
        (deficiencyId, transition) => {
            // Check for after images
            const fullDeficiency = state.deficiencies.find((d) => d.deficiencyId === deficiencyId);

            if (fullDeficiency?.afterImages?.length > 0) {
                transitionDeficiency(deficiencyId, transition);
                showNotification('Work marked as complete', 'success');
            } else {
                showNotification(
                    'Please upload "After" images before marking work as complete',
                    'warning'
                );
            }
        },
        [state.deficiencies, transitionDeficiency, showNotification]
    );

    const handleApprove = useCallback((deficiencyId, transition) => {
        setModalState({
            assignModalOpen: false,
            approveModalOpen: true,
            rejectModalOpen: false,
            activeTransition: transition,
        });
    }, []);

    const handleUpload = useCallback(() => {
        // First, find the After Images section
        const afterImagesSection = document.querySelector('[data-section="after-images"]');

        if (afterImagesSection) {
            // Scroll to the After Images section
            afterImagesSection.scrollIntoView({ behavior: 'smooth', block: 'center' });

            // Find the add button in the After Images section and simulate a click after scrolling
            setTimeout(() => {
                const addButton = afterImagesSection.querySelector('label[for^="upload-After"]');
                if (addButton) {
                    addButton.click();
                } else {
                    showNotification(
                        'Please click the "+" button in the After Images section',
                        'info'
                    );
                }
            }, 500); // Short delay to allow scrolling to complete
        } else {
            showNotification('Please upload "After" images in the section below', 'info');
        }
    }, [showNotification]);

    const handleReject = useCallback((deficiencyId, transition) => {
        setModalState({
            assignModalOpen: false,
            approveModalOpen: false,
            rejectModalOpen: true,
            activeTransition: transition,
        });
    }, []);

    // Modal completion handlers
    const completeAssign = useCallback(
        (assignData) => {
            if (!modalState.activeTransition || !deficiency) return;

            transitionDeficiency(deficiency.deficiencyId, modalState.activeTransition, {
                assignedToBusinessId: assignData.businessId,
                assignedToBusinessName: assignData.assignee,
            });

            setModalState((prev) => ({ ...prev, assignModalOpen: false }));
            showNotification('Deficiency assigned successfully', 'success');
        },
        [deficiency, modalState.activeTransition, transitionDeficiency, showNotification]
    );

    const completeApprove = useCallback(() => {
        if (!modalState.activeTransition || !deficiency) return;

        transitionDeficiency(deficiency.deficiencyId, modalState.activeTransition);
        setModalState((prev) => ({ ...prev, approveModalOpen: false }));
        showNotification('Deficiency approved successfully', 'success');
    }, [deficiency, modalState.activeTransition, transitionDeficiency, showNotification]);

    const completeReject = useCallback(async () => {
        if (!modalState.activeTransition || !deficiency || !rejectReason.trim()) return;

        try {
            // Determine rejection type based on the transition
            const isTerminalRejection =
                modalState.activeTransition === TRANSITION_ACTIONS.TRANSITION_1D_REJECT_SUBMITTED;

            if (isTerminalRejection) {
                // Terminal rejection from SUBMITTED - no attempt bounce
                await rejectTerminal(deficiency.deficiencyId, rejectReason.trim());
            } else {
                // Work rejection - bounce back with a new attempt
                // Requires activeAttemptId for stale-protection
                if (!deficiency.activeAttemptId) {
                    showNotification(
                        'No active attempt found — please refresh and try again.',
                        'error'
                    );
                    return;
                }
                await rejectWork(
                    deficiency.deficiencyId,
                    rejectReason.trim(),
                    deficiency.activeAttemptId
                );
            }

            setModalState((prev) => ({ ...prev, rejectModalOpen: false }));
            setRejectReason('');
            showNotification(
                isTerminalRejection ? 'Deficiency rejected' : 'Work rejected successfully',
                'success'
            );
        } catch (error) {
            showNotification('Error rejecting. Please try again.', 'error');
        }
    }, [
        deficiency,
        modalState.activeTransition,
        showNotification,
        rejectReason,
        rejectTerminal,
        rejectWork,
    ]);

    // Close modal handlers
    const closeAssignModal = useCallback(() => {
        setModalState((prev) => ({ ...prev, assignModalOpen: false }));
    }, []);

    const closeApproveModal = useCallback(() => {
        setModalState((prev) => ({ ...prev, approveModalOpen: false }));
    }, []);

    const closeRejectModal = useCallback(() => {
        setModalState((prev) => ({ ...prev, rejectModalOpen: false }));
        setRejectReason(''); // Clear the reason when closing
    }, []);

    const handleCloseNotification = useCallback(() => {
        setNotification((prev) => ({ ...prev, open: false }));
    }, []);

    if (!deficiency || !currentUser?.role) {
        return null;
    }

    const { deficiencyId, status } = deficiency;
    const hasAfterImages = deficiency.afterImages && deficiency.afterImages.length > 0;

    // Determine the most logical next action based on current state and data
    const getNextAction = () => {
        // Check permissions first to ensure user can perform actions
        const isInternalManager = isInternalManagerRole(currentUser.role);
        const canDoWork = isDeficiencyWorkRole(currentUser.role);
        const isExternalOwnerConsultant = isExternalOwnerConsultantRole(currentUser.role);

        switch (status) {
            case 'Submitted':
                // If it already has after images, mark complete is more efficient than accepting
                if (hasAfterImages && isInternalManager) {
                    const transition = getCorrectTransitionAction(
                        status,
                        'finish',
                        currentUser.role,
                        deficiency.internallyCreated
                    );
                    if (transition) {
                        return {
                            type: 'finish',
                            transition,
                            label: 'Mark Complete',
                            icon: <CheckCircleOutlineIcon />,
                            color: 'success',
                            handler: () => handleFinish(deficiencyId, transition),
                        };
                    }
                }

                // Otherwise, show both accept and reject options for PM/Super
                if (isInternalManager) {
                    const buttons = [];

                    // Check for accept transition
                    const acceptTransition = getCorrectTransitionAction(
                        status,
                        'accept',
                        currentUser.role,
                        deficiency.internallyCreated
                    );
                    if (acceptTransition) {
                        buttons.push({
                            type: 'accept',
                            transition: acceptTransition,
                            label: 'Accept',
                            icon: <DoneAllIcon />,
                            color: 'success',
                            handler: () => handleAccept(deficiencyId, acceptTransition),
                        });
                    }

                    // Check for terminal reject transition (SUBMITTED → REJECTED)
                    const rejectTransition = getCorrectTransitionAction(
                        status,
                        'reject_terminal',
                        currentUser.role,
                        deficiency.internallyCreated
                    );
                    if (rejectTransition) {
                        buttons.push({
                            type: 'reject',
                            transition: rejectTransition,
                            label: 'Reject',
                            icon: <ThumbDownIcon />,
                            color: 'error',
                            handler: () => handleReject(deficiencyId, rejectTransition),
                        });
                    }

                    if (buttons.length > 0) {
                        return {
                            type: 'split',
                            buttons,
                        };
                    }
                }
                break;

            case 'Accepted':
                // If it has after images already, mark complete is more valuable than assigning
                if (hasAfterImages && isInternalManager) {
                    const transition = getCorrectTransitionAction(
                        status,
                        'finish',
                        currentUser.role,
                        deficiency.internallyCreated
                    );
                    if (transition) {
                        return {
                            type: 'finish',
                            transition,
                            label: 'Mark Complete',
                            icon: <CheckCircleOutlineIcon />,
                            color: 'success',
                            handler: () => handleFinish(deficiencyId, transition),
                        };
                    }
                }

                // Otherwise assigning is the natural next step
                if (isInternalManager) {
                    const transition = getCorrectTransitionAction(
                        status,
                        'assign',
                        currentUser.role,
                        deficiency.internallyCreated
                    );
                    if (transition) {
                        return {
                            type: 'assign',
                            transition,
                            label: 'Assign',
                            icon: <PersonAddIcon />,
                            color: 'secondary',
                            handler: () => handleAssign(deficiencyId, transition),
                        };
                    }
                }
                break;

            case 'Assigned':
                // For assigned deficiencies, mark complete is the next step if images exist
                if (hasAfterImages && (isInternalManager || canDoWork)) {
                    const transition = getCorrectTransitionAction(
                        status,
                        'finish',
                        currentUser.role,
                        deficiency.internallyCreated
                    );
                    if (transition) {
                        return {
                            type: 'finish',
                            transition,
                            label: 'Mark Complete',
                            icon: <CheckCircleOutlineIcon />,
                            color: 'success',
                            handler: () => handleFinish(deficiencyId, transition),
                        };
                    }
                }

                // If no after images, prompt to upload them
                if (isInternalManager || canDoWork) {
                    return {
                        type: 'upload',
                        label: 'Upload After Images',
                        icon: <PublishIcon />,
                        color: 'info',
                        handler: () => handleUpload(),
                    };
                }
                break;

            case 'Work Completed':
                // If PM or Super, show both approve and reject buttons
                if (isInternalManager) {
                    const buttons = [];

                    // Check for approve transition
                    const approveTransition = getCorrectTransitionAction(
                        status,
                        'approve',
                        currentUser.role,
                        deficiency.internallyCreated
                    );
                    if (approveTransition) {
                        buttons.push({
                            type: 'approve',
                            transition: approveTransition,
                            label: 'Approve',
                            icon: <ThumbUpAltIcon />,
                            color: 'success',
                            handler: () => handleApprove(deficiencyId, approveTransition),
                        });
                    }

                    // Check for work reject transition (WORK_COMPLETED → ASSIGNED)
                    const rejectTransition = getCorrectTransitionAction(
                        status,
                        'reject_work',
                        currentUser.role,
                        deficiency.internallyCreated
                    );
                    if (rejectTransition) {
                        buttons.push({
                            type: 'reject',
                            transition: rejectTransition,
                            label: 'Reject',
                            icon: <ThumbDownIcon />,
                            color: 'error',
                            handler: () => handleReject(deficiencyId, rejectTransition),
                        });
                    }

                    if (buttons.length > 0) {
                        return {
                            type: 'split',
                            buttons,
                        };
                    }
                }
                break;

            case 'Internally Signed Off':
                // If external user, show both final approval and reject buttons
                if (isExternalOwnerConsultant) {
                    const buttons = [];

                    // Check for approve transition (Final Approval)
                    const approveTransition = getCorrectTransitionAction(
                        status,
                        'approve',
                        currentUser.role,
                        deficiency.internallyCreated
                    );
                    if (approveTransition) {
                        buttons.push({
                            type: 'approve',
                            transition: approveTransition,
                            label: 'Final Approval',
                            icon: <DoneAllIcon />,
                            color: 'success',
                            handler: () => handleApprove(deficiencyId, approveTransition),
                        });
                    }

                    // Check for work reject transition (bounce-back to ASSIGNED)
                    const rejectTransition = getCorrectTransitionAction(
                        status,
                        'reject_work',
                        currentUser.role,
                        deficiency.internallyCreated
                    );
                    if (rejectTransition) {
                        buttons.push({
                            type: 'reject',
                            transition: rejectTransition,
                            label: 'Reject Work',
                            icon: <ThumbDownIcon />,
                            color: 'error',
                            handler: () => handleReject(deficiencyId, rejectTransition),
                        });
                    }

                    if (buttons.length > 0) {
                        return buttons.length === 1 ? buttons[0] : { type: 'split', buttons };
                    }
                }
                break;

            case 'Completed':
                // Terminal state, no action button needed
                return null;

            default:
                return null;
        }

        // If no matching action found
        return null;
    };

    const nextAction = getNextAction();

    if (!nextAction) {
        return null;
    }

    // Common button styles for both split and single buttons
    const getButtonStyles = (type) => ({
        animation: 'pulse 1.5s',
        '@keyframes pulse': {
            '0%': { boxShadow: '0 0 0 0 rgba(66, 133, 244, 0.4)' },
            '70%': { boxShadow: '0 0 0 10px rgba(66, 133, 244, 0)' },
            '100%': { boxShadow: '0 0 0 0 rgba(66, 133, 244, 0)' },
        },
        // Apply different shadow colors based on action type
        ...(type === 'assign' && {
            boxShadow: '0 4px 10px rgba(156, 39, 176, 0.3)', // purple shadow for assign
        }),
        ...(type === 'finish' && {
            boxShadow: '0 4px 10px rgba(76, 175, 80, 0.3)', // green shadow for finish
        }),
        ...(type === 'approve' && {
            boxShadow: '0 4px 10px rgba(76, 175, 80, 0.3)', // green shadow for approve
        }),
        ...(type === 'reject' && {
            boxShadow: '0 4px 10px rgba(244, 67, 54, 0.3)', // red shadow for reject
        }),
        ...(type === 'upload' && {
            boxShadow: '0 4px 10px rgba(3, 169, 244, 0.3)', // light blue for upload
        }),
    });

    const actionControl =
        nextAction.type === 'split' ? (
            <Stack
                direction={nextAction.buttons.length === 2 ? 'row' : 'column'}
                spacing={1}
                width="100%"
            >
                {nextAction.buttons.map((button) => (
                    <Button
                        key={button.type}
                        onClick={button.handler}
                        variant="contained"
                        color={button.color || 'primary'}
                        startIcon={button.icon || <PlayArrowIcon />}
                        fullWidth
                        size="large"
                        sx={{
                            ...getButtonStyles(button.type),
                            ...buttonProps.sx,
                        }}
                        {...buttonProps}
                    >
                        {button.label}
                    </Button>
                ))}
            </Stack>
        ) : (
            <Button
                onClick={nextAction.handler}
                variant="contained"
                color={nextAction.color || 'primary'}
                startIcon={nextAction.icon || <PlayArrowIcon />}
                fullWidth
                size="large"
                sx={{
                    ...getButtonStyles(nextAction.type),
                    ...buttonProps.sx,
                }}
                {...buttonProps}
            >
                {nextAction.label}
            </Button>
        );

    const content = (
        <>
            {actionControl}

            {/* Shared Modals */}
            <AssigneeModal
                open={modalState.assignModalOpen}
                onClose={closeAssignModal}
                onSubmit={completeAssign}
            />

            <Dialog open={modalState.approveModalOpen} onClose={closeApproveModal}>
                <DialogTitle>Confirm Approval</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to sign off on this work?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeApproveModal}>Cancel</Button>
                    <Button onClick={completeApprove} color="primary" autoFocus>
                        Approve
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={modalState.rejectModalOpen} onClose={closeRejectModal}>
                <DialogTitle>
                    {modalState.activeTransition ===
                    TRANSITION_ACTIONS.TRANSITION_1D_REJECT_SUBMITTED
                        ? 'Reject Deficiency'
                        : 'Reject Work'}
                </DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        {modalState.activeTransition ===
                        TRANSITION_ACTIONS.TRANSITION_1D_REJECT_SUBMITTED
                            ? 'Please provide a reason for rejecting this deficiency:'
                            : 'Please provide a reason for rejecting this work:'}
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        placeholder={
                            modalState.activeTransition ===
                            TRANSITION_ACTIONS.TRANSITION_1D_REJECT_SUBMITTED
                                ? 'e.g., Invalid deficiency submission...'
                                : 'e.g., Work does not meet specifications...'
                        }
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRejectModal}>Cancel</Button>
                    <Button
                        onClick={completeReject}
                        color="error"
                        variant="contained"
                        disabled={!rejectReason.trim()}
                    >
                        {modalState.activeTransition ===
                        TRANSITION_ACTIONS.TRANSITION_1D_REJECT_SUBMITTED
                            ? 'Reject'
                            : 'Reject Work'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={notification.open}
                autoHideDuration={3000}
                onClose={handleCloseNotification}
            >
                <Alert
                    onClose={handleCloseNotification}
                    severity={notification.severity}
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </>
    );

    if (renderSection) {
        return (
            <Box
                sx={{
                    p: 2,
                    backgroundColor: '#FFFFFF',
                    borderBottom: '1px solid #F0F0F0',
                }}
            >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Next Action
                </Typography>
                {content}
            </Box>
        );
    }

    return content;
};

export default NextActionButton;
